import wrapper from './api';
import { trace } from '../utils/notifyParent';
import { ApiError, MerchantWalletError } from '../utils/errors';
import { TRACING_EVENTS } from '../../shared/constants';
import { MerchantWalletMapper } from '../mappers/merchantWalletMapper';

function merchantWalletService({ token }) {
	const onError = (error => {
		const serviceIsUnavailable = error instanceof MerchantWalletError
			&& (
				error.isNetworkError()
				|| error.isHTTPServiceUnavailable()
				|| error.isHTTPGatewayTimeout()
			);
		const details = error instanceof ApiError && error.details;

		if (error instanceof MerchantWalletError && error.isHTTPNotFoundError()) {
			return;
		}

		if (serviceIsUnavailable) {
			trace(TRACING_EVENTS.MERCHANT_WALLET_SERVICE_UNAVAILABLE, {
				details,
			});
		} else {
			trace(TRACING_EVENTS.SERVER_ERROR, {
				type: 'Merchant Wallet service',
				reason: error.message,
				details,
			});
		}
	});

	const api = wrapper({
		request: {
			baseURL: '/merchant-wallet/api/v1/client',
			withCredentials: false,
			headers: {
				Authorization: `Bearer ${token}`,
				'Content-type': 'application/json',
			},
			errorHandled: true,
		},
		error: MerchantWalletError,
		onError,
	});

	return {
		getCustomer,
		upsertCustomer,
		createTokenization,
		confirmTokenization,
		getAvailablePaymentMethods,
		getMerchantAvailablePaymentMethods,
		patchPaymentMethod,
		patchBillingAddress,
	};

	/**
	 * Get the current customer from Merchant Wallet Client API.
	 * @returns {Promise<Object|null>}
	 */
	async function getCustomer() {
		const response = await api.get('/customer');
		return MerchantWalletMapper.mapCustomerResponse(response.data);
	}

	/**
	 * Create or update the current customer.
	 * @param {Object} params
	 * @param {string} [params.email]
	 * @param {string} [params.phoneNumber]
	 * @param {Object} params.billingAddress
	 * @returns {Promise<Object|null>}
	 */
	async function upsertCustomer(params) {
		const requestData = MerchantWalletMapper.mapUpsertCustomerRequest(params);
		const response = await api.put('/customer', requestData);
		return MerchantWalletMapper.mapCustomerResponse(response.data);
	}

	/**
	 * Create a payment method tokenization.
	 * @param {Object} params
	 * @param {string} [params.source]
	 * @param {string} params.redirectUrl
	 * @param {string} [params.returnUrl]
	 * @returns {Promise<Object|null>}
	 */
	async function createTokenization(params) {
		const requestData = MerchantWalletMapper.mapCreateTokenizationRequest(params);
		const response = await api.post('/customer/payment-methods/tokenizations', requestData);
		return MerchantWalletMapper.mapTokenizationResponse(response.data);
	}

	/**
	 * Confirm a payment method tokenization.
	 * @param {Object} params
	 * @param {string} params.tokenizationId
	 * @param {Object} [params.paymentMethod]
	 * @returns {Promise<Object|null>}
	 */
	async function confirmTokenization({ tokenizationId, paymentMethod }) {
		const requestData = MerchantWalletMapper.mapConfirmTokenizationRequest(paymentMethod);
		const response = await api.post(
			`/customer/payment-methods/tokenizations/${tokenizationId}/confirm`,
			requestData || {}
		);
		return MerchantWalletMapper.mapConfirmTokenizationResponse(response.data);
	}

	/**
	 * Get available and active payment methods for the current customer.
	 * @returns {Promise<Object|null>}
	 */
	async function getAvailablePaymentMethods() {
		try {
			const response = await api.get('/customer/payment-methods/available');
			return MerchantWalletMapper.mapAvailablePaymentMethodsResponse(response.data);
		} catch (err) {
			if (err instanceof MerchantWalletError && err.isHTTPNotFoundError()) {
				return null;
			}

			throw err;
		}
	}

	/**
	 * Get payment methods available for the merchant in a country.
	 * Used when no MW customer exists yet (e.g. first purchase).
	 * MW returns country-configured types in `available`, including payout, SU PM, and SUBA.
	 * @param {Object} params
	 * @param {string} params.countryCode
	 * @returns {Promise<Object|null>}
	 */
	async function getMerchantAvailablePaymentMethods({ countryCode }) {
		try {
			const response = await api.get('/merchant/payment-methods/available', {
				params: { country_code: countryCode },
			});
			return MerchantWalletMapper.mapAvailablePaymentMethodsResponse(response.data);
		} catch (err) {
			if (err instanceof MerchantWalletError && err.isHTTPNotFoundError()) {
				return null;
			}

			throw err;
		}
	}

	/**
	 * Patch a payment method (e.g. set as default).
	 * @param {Object} params
	 * @param {string} params.paymentMethodId
	 * @param {boolean} params.default
	 * @returns {Promise<Object|null>}
	 */
	async function patchPaymentMethod({ paymentMethodId, default: isDefault }) {
		const response = await api.patch(
			`/customer/payment-methods/${paymentMethodId}`,
			{ default: isDefault },
		);
		return MerchantWalletMapper.mapPaymentMethodResponse(response.data);
	}

	/**
	 * Patch the current customer's billing address.
	 * @param {Object} params
	 * @param {Object} [params.billingAddress]
	 * @param {string} [params.vatNumber]
	 * @returns {Promise<Object|null>}
	 */
	async function patchBillingAddress(params) {
		const requestData = MerchantWalletMapper.mapPatchBillingAddressRequest(params);
		const response = await api.patch('/customer/billing-address', requestData);
		return MerchantWalletMapper.mapCustomerResponse(response.data);
	}
}

export default merchantWalletService;
